import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVancouverHappyHour() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-best-happy-hour";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Joe Fortes Seafood & Chop House",
    "section-2": "The Roof at Black+Blue",
    "section-3": "The Keefer Bar",
    "section-4": "Fanny Bay Oyster Bar",
    "section-5": "CóMO? Tapería",
    "section-6": "Tacofino Oasis",
    "section-7": "Hydra Estiatorio Mediterranean",
    "section-8": "D/6 Bar & Lounge",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://news.gov.bc.ca/releases/happy-hour-minimum-pricing",
      label: "Government of British Columbia – Happy Hour and Minimum Pricing",
    },
    {
      url: "https://www.bcliquorstores.com",
      label: "BC Liquor Control - Legal Drinking Age Information",
    },
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver – Dining & Nightlife",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We'll match each section (where we mention a location) with an id and coordinates.
  const locations = [
    {
      id: "section-1",
      lat: 49.28493,
      lng: -123.12473,
      popupText: "Joe Fortes Seafood & Chop House",
    },
    {
      id: "section-2",
      lat: 49.28454,
      lng: -123.12284,
      popupText: "The Roof at Black+Blue",
    },
    {
      id: "section-3",
      lat: 49.27965,
      lng: -123.10098,
      popupText: "The Keefer Bar",
    },
    {
      id: "section-4",
      lat: 49.27990,
      lng: -123.11230,
      popupText: "Fanny Bay Oyster Bar",
    },
    {
      id: "section-5",
      lat: 49.26585,
      lng: -123.09834,
      popupText: "CóMO? Tapería",
    },
    {
      id: "section-6",
      lat: 49.26640,
      lng: -123.10450,
      popupText: "Tacofino Oasis",
    },
    {
      id: "section-7",
      lat: 49.28500,
      lng: -123.11514,
      popupText: "Hydra Estiatorio Mediterranean",
    },
    {
      id: "section-8",
      lat: 49.27670,
      lng: -123.11145,
      popupText: "D/6 Bar & Lounge",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver’s Best Happy Hour Deals You Can’t Miss</title>
        <meta
          name="description"
          content="Discover Vancouver’s top happy hour spots for unbeatable drinks and dining deals. Tips, hours, addresses, and local regulations included."
        />
        <meta
          name="keywords"
          content="Vancouver happy hour, best deals, cheap drinks, restaurant guide"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-best-happy-hour.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver’s Best Happy Hour Deals You Can’t Miss"
        />
        <meta
          property="og:description"
          content="Discover Vancouver’s top happy hour spots for unbeatable drinks and dining deals. Tips, hours, addresses, and local regulations included."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-best-happy-hour.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-best-happy-hour.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver’s Best Happy Hour Deals You Can’t Miss"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver’s top happy hour spots for unbeatable drinks and dining deals. Tips, hours, addresses, and local regulations included."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-best-happy-hour.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="An assortment of cocktails and appetizers typical of a Vancouver happy hour."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s happy hour scene is bursting with hidden gems and
              unbeatable drink-and-dine deals. Whether you’re craving fresh
              oysters, inventive craft cocktails, or mouthwatering tapas, these
              limited-time specials make it easy to sample the city’s flavors
              without breaking the bank. Below, we’ve rounded up some of
              Vancouver’s best happy hour spots, complete with addresses,
              coordinates, operating hours, and approximate costs—so you can
              toast with confidence.
            </p>

            {/* SECTION 1: Joe Fortes Seafood & Chop House */}
            <h2 id="section-1">Joe Fortes Seafood & Chop House</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 777 Thurlow Street, Vancouver, BC V6E
              3V5{" "}
              <Marker
                coordY={49.28493}
                coordX={-123.12473}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              An iconic spot for fresh oysters and classic cocktails, Joe Fortes
              boasts a lively rooftop patio and bustling bar scene. Their daily
              happy hour (3:00–5:00 PM) features $1.50 oysters, $5–$8 drinks,
              and small plates like crispy calamari or truffle fries. Arrive
              early to snag a seat at the bar or on their popular rooftop deck.
            </p>

            {/* SECTION 2: The Roof at Black+Blue */}
            <h2 id="section-2">The Roof at Black+Blue</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 1032 Alberni Street, Vancouver, BC V6E
              1A3{" "}
              <Marker
                coordY={49.28454}
                coordX={-123.12284}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Perched atop Black+Blue steakhouse, this trellis-covered rooftop
              lounge offers a chic open-air dining experience. Enjoy daily
              happy hour from 2:30–5:30 PM with $7 draft beer, $7–$9 wines, and
              $8–$10 cocktails. Steakhouse nachos, truffle fries, and oyster
              specials often top the menu, making it a stylish post-work
              hangout.
            </p>

            {/* SECTION 3: The Keefer Bar */}
            <h2 id="section-3">The Keefer Bar</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 135 Keefer Street, Vancouver, BC V6A
              1X3{" "}
              <Marker
                coordY={49.27965}
                coordX={-123.10098}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              This Chinatown gem is famed for its apothecary-inspired cocktails
              and dim sum bites. Ranked among North America’s top bars, The
              Keefer offers Sunday–Friday happy hour (4:00–6:00 PM) with
              half-priced signature cocktails and discounted bar snacks like
              dumplings. Sip an expertly crafted Keefer 75 for under $8 and soak
              up the moody, eclectic atmosphere.
            </p>

            {/* SECTION 4: Fanny Bay Oyster Bar */}
            <h2 id="section-4">Fanny Bay Oyster Bar</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 762 Cambie Street, Vancouver, BC V6B
              2P2{" "}
              <Marker
                coordY={49.27990}
                coordX={-123.11230}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Operated by a local shellfish farm, Fanny Bay Oyster Bar features
              daily happy hour (3:00–6:00 PM) with fresh West Coast oysters for
              $1.50 apiece. Complement your shellfish with craft beer or house
              wine ($6–$9) and tasty seafood appetizers like fried oysters,
              chowder, or fish tacos. Seafood lovers, take note—this is a prime
              spot for “cheap” premium oysters.
            </p>

            {/* SECTION 5: CóMO? Tapería */}
            <h2 id="section-5">CóMO? Tapería</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 201 East 7th Avenue, Vancouver, BC V5T
              0B4{" "}
              <Marker
                coordY={49.26585}
                coordX={-123.09834}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Step into Spain at this energetic tapas bar in Mount Pleasant.
              Their one-hour happy hour (4:00–5:00 PM daily) rewards punctuality
              with a free tapa when you order a drink at the bar. All food is
              25% off, and select wine bottles are 40% off—an excellent way to
              sample authentic Iberian bites like conservas, patatas bravas, and
              cured meats without the usual splurge.
            </p>

            {/* SECTION 6: Tacofino Oasis */}
            <h2 id="section-6">Tacofino Oasis</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 8 East 5th Avenue, Vancouver, BC V5T
              1G8{" "}
              <Marker
                coordY={49.26640}
                coordX={-123.10450}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Known for its laid-back vibe and delicious Baja-style tacos,
              Tacofino Oasis in Mount Pleasant hosts a weekday happy hour
              (3:00–6:00 PM). Classic margaritas run around $6, fish or carnitas
              tacos get discounted $2 off, and guacamole & chips comes at a
              bargain. Check out their themed daily specials on Instagram for
              variety—like Margarita Mondays or vegetarian taco Wednesdays.
            </p>

            {/* SECTION 7: Hydra Estiatorio Mediterranean */}
            <h2 id="section-7">Hydra Estiatorio Mediterranean</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 475 Howe Street, Vancouver, BC V6C 2B3{" "}
              <Marker
                coordY={49.28500}
                coordX={-123.11514}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Tucked inside the EXchange Hotel, Hydra serves upscale Greek
              cuisine in an airy, modern setting. Their happy hour (3:00–6:00 PM
              in the lounge) is a hidden secret, offering $1.50 oysters,
              $6 Mythos beer or Greek wine, and small plates for around $10–12.
              A classy choice for seafood and Mediterranean flavors at a fraction
              of regular dinner prices.
            </p>

            {/* SECTION 8: D/6 Bar & Lounge */}
            <h2 id="section-8">D/6 Bar & Lounge</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 39 Smithe Street, Vancouver, BC V6B
              0R3{" "}
              <Marker
                coordY={49.27670}
                coordX={-123.11145}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Nestled on the sixth floor of the Douglas hotel at Parq Vancouver,
              D/6 features a stylish indoor/outdoor lounge with fire pits,
              skyline views, and a hidden library area. Happy hour (5:00–6:30
              PM) includes $7 Asahi, $12 sake or house wine, and cocktail
              specials (~$13). Snack on wagyu beef mini hot dogs or spicy
              edamame in this chic, rooftop-style retreat.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              <strong>Legal Drinking Age:</strong> You must be at least 19 years
              old to purchase or consume alcohol in British Columbia. Expect
              venues to ask for valid ID if you appear under 25.
            </p>
            <p>
              <strong>Minimum Drink Pricing:</strong> Happy hours in BC became
              legal in 2014, but there is a mandatory minimum drink price of
              $3.00 CAD. All the deals mentioned comply with this. 
            </p>
            <p>
              <strong>Responsible Enjoyment:</strong> Plan a safe ride home or
              use public transit/rideshares. Vancouver prohibits drinking in
              most public spaces, so finish your beverage before leaving a
              licensed patio. Venues may refuse service if you appear overly
              intoxicated.
            </p>
            <p>
              <strong>Check for Updates:</strong> Because menus and times can
              change, always verify details on a restaurant’s official site or
              social media page before you go.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are happy hour specials available on weekends?</h3>
              <p>
                Many of these spots offer weekend happy hours (e.g., Joe Fortes,
                Fanny Bay). Others may limit happy hour to weekdays. Always
                check the venue’s website or call to confirm current hours.
              </p>

              <h3>2. Is food required to order happy hour drinks?</h3>
              <p>
                Generally, no. BC liquor laws don’t mandate a food purchase with
                alcoholic drinks. However, sampling discounted food specials can
                enhance your overall experience (and help pace your drinking).
              </p>

              <h3>3. Can I make reservations for happy hour?</h3>
              <p>
                Most places operate happy hour on a first-come, first-served
                basis, especially at the bar or lounge area. If you have a large
                group, consider arriving early or calling ahead to see if the
                venue allows reservations during happy hour.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s happy hour culture lets you enjoy top-tier cuisine and
              inventive drinks at wallet-friendly prices. From $1.50 oysters to
              half-priced tapas, these deals capture the city’s culinary spirit
              in a budget-friendly way. We hope this guide helps you zero in on
              the happy hour that matches your vibe—remember to drink
              responsibly, plan safe transportation, and have fun exploring
              Vancouver’s diverse food scene.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}